import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PublicHeader from '../../components/ui/PublicHeader';
import PublicFooter from '../../components/ui/PublicFooter';
import Icon from '../../components/AppIcon';
import FailedIcon from './components/FailedIcon';
import TransactionDetails from './components/TransactionDetails';
import { followupStatus } from '../../api/followup';

const PaymentFailed = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tid = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('tid');
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!tid) {
      setLoading(false);
      setError('شناسه پیگیری یافت نشد.');
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await followupStatus(tid);
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            'دریافت اطلاعات تراکنش با مشکل مواجه شد. لطفاً دوباره تلاش کنید.',
          );
          console.error('Followup status fetch failed:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [tid]);

  const handleRetry = () => {
    navigate('/message-sending-interface');
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  const transaction = data?.transaction;
  const msgReq = data?.msg_req;

  return (
    <>
      <Helmet>
        <title>پرداخت ناموفق - Khejalatiam</title>
        <meta
          name="description"
          content="متاسفانه تراکنش شما با مشکل مواجه شد. لطفاً دوباره تلاش کنید یا با پشتیبانی تماس بگیرید"
        />
      </Helmet>
      <div
        className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50"
        dir="rtl"
      >
        <PublicHeader />

        <main className="container mx-auto px-4 py-8 max-w-4xl">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-6">
              <div className="h-12 w-12 rounded-full border-4 border-t-transparent animate-spin border-red-500" />
              <p className="text-lg text-gray-600">
                در حال دریافت اطلاعات تراکنش...
              </p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                <Icon name="AlertCircle" size={32} color="#dc2626" />
              </div>
              <p className="text-lg text-gray-700">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all"
              >
                تلاش مجدد
              </button>
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              <div className="text-center space-y-4 md:space-y-6">
                <FailedIcon />

                <div className="space-y-2 md:space-y-3">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                    پرداخت ناموفق بود
                  </h1>
                </div>
              </div>

              <TransactionDetails
                amount={transaction?.amount}
                timestamp={transaction?.updated_at}
              />

              <div className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-4 md:space-y-6">
                <div className="space-y-2 md:space-y-3">
                  <h3 className="text-lg md:text-2xl font-bold text-gray-800">
                    آماده‌اید دوباره تلاش کنید؟
                  </h3>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white rounded-xl font-semibold transition-all"
                  >
                    تلاش مجدد
                    <Icon name="RotateCcw" size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>

        <PublicFooter />
      </div>
    </>
  );
};

export default PaymentFailed;
